import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { meetPersonSchema } from '@/types/meetPerson.types';
import { VERIFY_STATUS } from '../constants/billing.constants';

const rangeSchema = z.object({ low: z.number(), high: z.number() });

export const billingLimitsSchema = z.object({
  dcrFare: rangeSchema,
  derFare: rangeSchema,
  specimenQtyHigh: z.number(),
  approveDayHigh: z.number(),
});

export const billingClaimTotalsSchema = z.object({
  members: z.number(),
  claimed: z.number(),
});

export const billingApprovalTotalsSchema = billingClaimTotalsSchema.extend({
  approved: z.number(),
});

export type BillingClaimTotals = z.infer<typeof billingClaimTotalsSchema>;
export type BillingApprovalTotals = z.infer<typeof billingApprovalTotalsSchema>;

const decisionSchema = z.enum(['pending', 'accepted', 'rejected']);

export const offlineDcrRowSchema = z.object({
  recordId: z.coerce.number(),
  dcrDate: z.string(),
  userId: z.coerce.number(),
  name: z.string(),
  userMobile: z.string().nullable(),
  designationName: z.string().nullable(),
  onBehalfOf: z.string().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteNo: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteThana: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  instituteDivision: z.string().nullable(),
  meetPeople: z.array(meetPersonSchema),
  areaFrom: z.string().nullable(),
  areaTo: z.string().nullable(),
  comments: z.string().nullable(),
  timeFrom: z.string().nullable(),
  timeTo: z.string().nullable(),
  workType: z.string().nullable(),
  workTypeCode: z.string().nullable(),
  transportTypeId: z.coerce.number().nullable(),
  transportType: z.string().nullable(),
  approveTransportTypeId: z.coerce.number().nullable(),
  fareAmount: z.number(),
  approveFareAmount: z.number(),
  maxSpecimenQty: z.number(),
  isQtyFlagged: z.boolean(),
  qtyTotal: z.number(),
  status: decisionSchema,
  approveStatus: decisionSchema,
  entryAgeDays: z.number(),
});

export const offlineDcrResponseSchema = z.object({
  data: z.array(offlineDcrRowSchema),
  meta: paginationMetaSchema.extend({
    limits: billingLimitsSchema,
    totals: billingClaimTotalsSchema,
  }),
});

export const offlineDerRowSchema = z.object({
  detailId: z.coerce.number(),
  expenseId: z.coerce.number(),
  expenseDate: z.string(),
  enteredAt: z.string().nullable(),
  userId: z.coerce.number(),
  name: z.string(),
  userMobile: z.string().nullable(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  expenseTypeId: z.coerce.number().nullable(),
  expenseType: z.string().nullable(),
  approveExpenseTypeId: z.coerce.number().nullable(),
  amount: z.number(),
  approveAmount: z.number(),
  vendor: z.string().nullable(),
  vendorLat: z.number().nullable(),
  vendorLon: z.number().nullable(),
  comments: z.string().nullable(),
  fromLocation: z.string().nullable(),
  toLocation: z.string().nullable(),
  fileName: z.string().nullable(),
  hasAttachment: z.boolean(),
  status: decisionSchema,
  approveStatus: decisionSchema,
  entryAgeDays: z.number(),
});

export const offlineDerResponseSchema = z.object({
  data: z.array(offlineDerRowSchema),
  meta: paginationMetaSchema.extend({
    limits: billingLimitsSchema,
    totals: billingClaimTotalsSchema,
  }),
});

export const expenseHeaderSchema = z.object({
  expenseId: z.coerce.number(),
  expenseDate: z.string(),
  enteredAt: z.string().nullable(),
  userId: z.coerce.number(),
  name: z.string(),
  userMobile: z.string().nullable(),
  designationName: z.string().nullable(),
  fiscalYear: z.string().nullable(),
  approveStatus: decisionSchema,
  reasonNo: z.coerce.number().nullable(),
  remarks: z.string().nullable(),
  isLocked: z.boolean(),
  lineCount: z.number(),
  claimedTotal: z.number(),
  approvedTotal: z.number(),
});

export const expenseHeaderLineSchema = z.object({
  detailId: z.coerce.number(),
  expenseTypeId: z.coerce.number().nullable(),
  expenseType: z.string().nullable(),
  approveExpenseTypeId: z.coerce.number().nullable(),
  approveExpenseType: z.string().nullable(),
  amount: z.number(),
  approveAmount: z.number(),
  approveStatus: decisionSchema,
  status: decisionSchema,
  vendor: z.string().nullable(),
  comments: z.string().nullable(),
  fromLocation: z.string().nullable(),
  toLocation: z.string().nullable(),
  fileName: z.string().nullable(),
  hasAttachment: z.boolean(),
});

export const expenseHeaderResponseSchema = z.object({
  data: z.array(expenseHeaderSchema),
  meta: paginationMetaSchema.extend({ totals: billingApprovalTotalsSchema }),
});

export const expenseApprovalDetailSchema = expenseHeaderSchema.extend({
  lines: z.array(expenseHeaderLineSchema),
});

export const expenseTypeSchema = z.object({
  id: z.coerce.number(),
  code: z.string().nullable(),
  name: z.string(),
});

export const expenseTypeListSchema = z.array(expenseTypeSchema);

export const transportTypeSchema = z.object({
  id: z.coerce.number(),
  code: z.string().nullable(),
  name: z.string(),
});

export const transportTypeListSchema = z.array(transportTypeSchema);

export const dcrEntryLineSchema = z.object({
  lineId: z.coerce.number(),
  lineKind: z.enum(['specimen', 'promo', 'contact']),
  item: z.string().nullable(),
  whom: z.string().nullable(),
  qty: z.number().nullable(),
  approveQty: z.number().nullable(),
  personName: z.string().nullable(),
  contact: z.string().nullable(),
  isOnBehalf: z.boolean(),
  onBehalfMobile: z.string().nullable(),
  designation: z.string().nullable(),
  subject: z.string().nullable(),
  totalStudents: z.number().nullable(),
  approxSaleQty: z.number().nullable(),
  witness: z.string().nullable(),
});

export const dcrEntryDetailSchema = z.object({
  recordId: z.coerce.number(),
  dcrDate: z.string(),
  workType: z.string().nullable(),
  workTypeCode: z.string().nullable(),
  memberName: z.string(),
  memberMobile: z.string().nullable(),
  refZmName: z.string().nullable(),
  refZmMobile: z.string().nullable(),
  areaFrom: z.string().nullable(),
  areaTo: z.string().nullable(),
  timeFrom: z.string().nullable(),
  timeTo: z.string().nullable(),
  transportType: z.string().nullable(),
  fareAmount: z.number(),
  instituteName: z.string().nullable(),
  lines: z.array(dcrEntryLineSchema),
});

const verificationSearchShape = {
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  reportingMemberId: z.coerce.number().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  isVerify: z.coerce.number().catch(VERIFY_STATUS.PENDING),
  search: z.string().optional(),
};

export const offlineDcrSearchSchema = z.object({
  ...verificationSearchShape,
  dcrTypeId: z.coerce.number().optional(),
});

export const offlineDerSearchSchema = z.object({
  ...verificationSearchShape,
  expenseTypeId: z.coerce.number().optional(),
  minAmount: z.coerce.number().optional(),
});

export const allCostTotalsSchema = z.object({
  dcrClaimed: z.number(),
  dcrApproved: z.number(),
  expenseClaimed: z.number(),
  expenseApproved: z.number(),
  totalClaimed: z.number(),
  totalApproved: z.number(),
});

export const allCostMemberSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  dcrClaimed: z.number(),
  dcrApproved: z.number(),
  expenseClaimed: z.number(),
  expenseApproved: z.number(),
  totalClaimed: z.number(),
  totalApproved: z.number(),
});

export const allCostResponseSchema = z.object({
  data: z.array(allCostMemberSchema),
  meta: paginationMetaSchema.extend({ totals: allCostTotalsSchema }),
});

export const allCostSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  search: z.string().optional(),
});

export type AllCostTotals = z.infer<typeof allCostTotalsSchema>;
export type AllCostMember = z.infer<typeof allCostMemberSchema>;
export type AllCostResponse = z.infer<typeof allCostResponseSchema>;
export type AllCostSearch = z.infer<typeof allCostSearchSchema>;

export const approvalSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  reportingMemberId: z.coerce.number().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  designationId: z.coerce.number().optional(),
  dcrTypeId: z.coerce.number().optional(),
  expenseTypeId: z.coerce.number().optional(),
  approveTypeNo: z.coerce.number().optional(),
  minFare: z.coerce.number().optional(),
  minAmount: z.coerce.number().optional(),
  search: z.string().optional(),
});

export type ApprovalSearch = z.infer<typeof approvalSearchSchema>;

export type DcrApprovalParams = Omit<ApprovalSearch, 'expenseTypeId' | 'minAmount'>;

export type DerApprovalParams = Omit<ApprovalSearch, 'dcrTypeId' | 'minFare'>;

export const expenseApprovalSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  fiscalYearId: z.coerce.number().optional(),
  approveTypeNo: z.coerce.number().catch(VERIFY_STATUS.PENDING),
  search: z.string().optional(),
});

export type ExpenseHeader = z.infer<typeof expenseHeaderSchema>;
export type ExpenseHeaderLine = z.infer<typeof expenseHeaderLineSchema>;
export type ExpenseHeaderResponse = z.infer<typeof expenseHeaderResponseSchema>;
export type ExpenseApprovalDetail = z.infer<typeof expenseApprovalDetailSchema>;
export type ExpenseType = z.infer<typeof expenseTypeSchema>;
export type ExpenseApprovalSearch = z.infer<typeof expenseApprovalSearchSchema>;
export type BillingLimits = z.infer<typeof billingLimitsSchema>;
export type OfflineDcrRow = z.infer<typeof offlineDcrRowSchema>;
export type OfflineDcrResponse = z.infer<typeof offlineDcrResponseSchema>;
export type OfflineDerRow = z.infer<typeof offlineDerRowSchema>;
export type OfflineDerResponse = z.infer<typeof offlineDerResponseSchema>;
export type TransportType = z.infer<typeof transportTypeSchema>;
export type DcrEntryLine = z.infer<typeof dcrEntryLineSchema>;
export type DcrEntryDetail = z.infer<typeof dcrEntryDetailSchema>;
export type OfflineDcrSearch = z.infer<typeof offlineDcrSearchSchema>;
export type OfflineDerSearch = z.infer<typeof offlineDerSearchSchema>;

export interface VerifyDcrPayload {
  accept: boolean;
  approveFareAmount?: number;
  transportTypeId?: number;
}

export interface VerifyDerPayload {
  accept: boolean;
  approveAmount?: number;
}

export interface ApproveDcrPayload {
  accept: boolean;
  approveFareAmount?: number;
  transportTypeId?: number;
  remarks?: string;
}

export interface ApproveDerPayload {
  accept: boolean;
  approveAmount?: number;
  approveExpenseTypeId?: number;
  remarks?: string;
}

export interface ApproveExpensePayload {
  accept: boolean;
  remarks?: string;
}

export interface AmendDcrPayload {
  fareAmount: number;
  remarks?: string;
}

export interface AmendDerPayload {
  amount: number;
  remarks?: string;
}

export interface WebExpenseLinePayload {
  expenseTypeId: number;
  amount: number;
  fromLocation: string;
  toLocation: string;
  vendor: string;
  comments: string;
}

export interface CreateWebExpensePayload {
  expenseDate: string;
  lines: WebExpenseLinePayload[];
}
